<template>
  <div class="auth auth__login-page">
    <b-img
      src="./static/base/teal-triangle.svg"
      alt="Anchor Loans - All in one spot"
      class="auth__teal-triangle"
    />
    <b-img
      src="./static/base/dotted-grid.svg"
      alt="Anchor Loans - All in one spot"
      class="auth__dotted-grid"
    />
    <div class="auth__login-card">
      <h2 class="auth__login-title">Welcome</h2>
      <VueFormGenerator
        :schema="schema"
        :model="model"
        :options="formOptions"
        class="auth__login-form"
        ref="loginForm"
        @keyup.enter.native="continueClick"
      />
      <b-button
        ref="submitButton"
        id="submit-buttom"
        class="auth__submit-button"
        primary
        @click="continueClick">
        Continue
      </b-button>
      <div class="auth__reset-link">
        <a href="#!" id="forgot-password-link" @click.prevent="onForgotPassword">
          Reset Password
        </a>
      </div>
      <div class="auth__signup-link" v-if="notInProduction">
        Don't have an account?
        <a href="#!" id="signup-link" @click.prevent="onSignUp">
          Sign up now
        </a>
      </div>
    </div>
    <ForgotPassModal
      ref="forgotPassModal"
      :email="this.model.email"
      @on-success="didResetPassword"
    />
    <ResendUnlockLink
      ref="resendUnlockLink"
      v-show="accountLocked"
      @email-sent="accountLocked = false"
    />
  </div>
</template>

<script>
import { LOGIN } from '@/vuex/types/actions';

import BasicHeader from '@/components/utils/BasicHeader';
import ForgotPassModal from '@/components/auth/forgot_pass/ForgotPassModal';
import ResendUnlockLink from '@/components/auth/unlock_account/ResendUnlockLink';

import PRE_LOGIN_SCHEMA from '@/components/auth/login/schema_pre_login';
import LOGIN_SCHEMA from '@/components/auth/login/schema';
import VERIFICATION_SCHEMA from '@/components/auth/login/schema_verification';

const FORM_CONFIG_PRE_LOGIN = 0;
const FORM_CONFIG_LOGIN = 1;
const FORM_CONFIG_VERIFICATION_CODE = 2;

const FORM_CONFIG_OPTIONS = {};
FORM_CONFIG_OPTIONS[FORM_CONFIG_PRE_LOGIN] = {
  schema: () => (PRE_LOGIN_SCHEMA.schema),
  options: PRE_LOGIN_SCHEMA.options,
  submitLabel: 'Continue',
};
FORM_CONFIG_OPTIONS[FORM_CONFIG_LOGIN] = {
  schema: emailChangedCallback => (LOGIN_SCHEMA.schema(emailChangedCallback)),
  options: LOGIN_SCHEMA.options,
  submitLabel: 'Login',
};
FORM_CONFIG_OPTIONS[FORM_CONFIG_VERIFICATION_CODE] = {
  schema: emailChangedCallback => (VERIFICATION_SCHEMA.schema(emailChangedCallback)),
  options: VERIFICATION_SCHEMA.options,
  submitLabel: 'Verify Code',
};

export default {
  components: {
    BasicHeader,
    ForgotPassModal,
    ResendUnlockLink,
  },
  data() {
    return {
      model: {
        email: null,
        password: null,
        code: null,
        registrationFlow: null,
      },
      formConfig: FORM_CONFIG_PRE_LOGIN,
      schema: FORM_CONFIG_OPTIONS[FORM_CONFIG_PRE_LOGIN].schema(this.emailChanged),
      formOptions: FORM_CONFIG_OPTIONS[FORM_CONFIG_PRE_LOGIN].options,
      headerData: {
        title: 'Sign In',
        headline: 'COME ON IN! We’re glad to have you here.',
        link: {
          text: 'Join Anchor Loans',
          prevText: 'Don\'t have an account?',
          routeName: 'Registration',
        },
      },
      accountLocked: false,
    };
  },
  computed: {
    notInProduction() {
      return process.env.NODE_ENV !== 'production';
    },
  },
  watch: {
    formConfig(value) {
      const formOption = FORM_CONFIG_OPTIONS[value];

      this.schema = formOption.schema(this.emailChanged);
      this.formOptions = formOption.options;
      this.$refs.submitButton.innerText = formOption.submitLabel;
    },
  },
  methods: {
    onForgotPassword() {
      this.displayForgotPassModal(true);
    },
    onSignUp() {
      this.$router.push({ name: 'Registration' });
    },
    displayForgotPassModal(showFirstStep) {
      this.$refs.forgotPassModal.show(showFirstStep);
    },
    didResetPassword() {
      this.$refs.forgotPassModal.hide();
      this.$notify({ group: 'alert', text: 'Your password has been successfully updated.' });
    },
    login() {
      const data = {
        email: this.model.email,
        password: this.model.password,
        campaignUrl: this.$store.getters.campaignUrl,
      };

      this.$reqs.post('/login', data, { error: false })
        .then((response) => {
          this.$store.dispatch(LOGIN, response.data);
        })
        .catch((error) => {
          const response = error.response;
          if (response && response.status === 400 && response.data.reset_password === true) {
            this.displayForgotPassModal(false);
          } else if (response && response.status === 400 &&
            typeof response.data.errors !== 'undefined' &&
            response.data.errors.includes('account_locked')) {
            this.accountLocked = true;
            this.onLockedOut();
          } else {
            this.$reqs.executeOnError(error);
          }
          this.model.password = null;
        });
    },
    onLockedOut() {
      this.displayUnlockAccountModal();
    },
    displayUnlockAccountModal() {
      this.$refs.resendUnlockLink.show();
    },
    checkAccount() {
      const data = {
        email: this.model.email,
      };

      this.$reqs.post('/check_account', data)
        .then((response) => {
          this.registrationFlow = response.data.registration_flow;
          if (this.registrationFlow) {
            this.formConfig = FORM_CONFIG_VERIFICATION_CODE;
          } else {
            this.formConfig = FORM_CONFIG_LOGIN;
          }
        });
    },
    verifyCode() {
      const data = {
        email: this.model.email,
        code: this.model.code,
        is_poc: this.registrationFlow === 'contributor',
      };

      this.$reqs.post('/check_account_verify_code', data)
        .then((response) => {
          if (response.data) {
            let routerName = null;
            if (this.registrationFlow === 'salesforce') {
              routerName = 'Invitation';
            } else if (this.registrationFlow === 'contributor') {
              routerName = 'Contributor';
            }
            this.$router.push({ name: routerName, params: response.data });
          } else {
            this.$notify({ group: 'alert', text: 'Your verification code is invalid.' });
          }
        });
    },
    continueClick() {
      if (!this.$refs.loginForm.validate()) { return; }

      if (this.formConfig === FORM_CONFIG_PRE_LOGIN) {
        this.checkAccount();
      } else if (this.formConfig === FORM_CONFIG_LOGIN) {
        this.login();
      } else if (this.formConfig === FORM_CONFIG_VERIFICATION_CODE) {
        this.verifyCode();
      }
    },
    emailChanged() {
      this.formConfig = FORM_CONFIG_PRE_LOGIN;
    },
  },
  created() {
    const emailParam = this.$route.query.email;

    if (emailParam) {
      this.model.email = emailParam;
    }
  },
};
</script>

<style scoped>
.auth__login-input {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5C7182;
  background: #FFFFFF;
  padding: 8px 12px;
  border: 1px solid #051F3A;
  border-radius: 4px;
  margin-bottom: 16px;
}

.auth__login-title {
  font-weight: 375;
  font-size: 32px;
  line-height: 40px;
  letter-spacing: 0.0025em;
  color: #000000;
  margin-bottom: 16px;
}

.auth__login-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  /* 68px for app bar and another for the padding */
  min-height: calc(100vh - 68px - 68px);
}

.auth__login-card {
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 4px;
  padding: 60px 40px;
  position: relative;
  z-index: 3;
}


.auth__submit-button {
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 20px;
  background-color: #2E78C2;
  color: #ffffff;
  border-radius: 4px;
  margin: 16px 0;
}

.auth__reset-link a,
.auth__signup-link a {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #2E78C2;
  margin-top: 10px;
}

.auth__signup-link {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5C7182;
}

/* Background style */
.auth__dotted-grid,
.auth__teal-triangle {
  position: absolute;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.auth__dotted-grid {
  top: 24%;
  right: 36%;
  z-index: 1;
}

.auth__teal-triangle {
  bottom: 18%;
  left: 29%;
  z-index: 2;
  max-width: 480px;
}

/* Bootstrap overrides */
.input-group-append button {
  background-color: #ffffff;
  border-left: 0;
}

input:-internal-autofill-selected {
  background-color: #ffffff;
}

@media (max-width: 767px) {
  .auth__dotted-grid,
  .auth__teal-triangle {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/auth/login/Login.vue